import { useState } from 'react';
import { Pressable, Text, View } from 'react-native';
import type { Reg } from '../store/types';
import type { Colecao, Ctx, ModDef, PainelProps } from './tipos';
import { dataBr, din, dinDe, dinSomas, junta, num, op, somaPorMoeda, temNum } from './util';
import { mudarCampos } from '../store/modulos';
import { Field } from '../components/ui';
import { C } from '../components/theme';
import { addDays, today } from '../lib/date';
import { lerValor } from '../lib/moeda';
import { N_, t } from '../lib/i18n';
import { acaoRelatorioPdf, dadosPdf, porDataPdf, secaoPdf } from '../lib/pdfFicha';
import type { BlocoPdf } from '../lib/pdf';

const MANUT = [N_('Troca de óleo'), N_('Rodízio de pneus'), N_('Revisão'), N_('Alinhamento e balanceamento'), N_('Pneus novos'), N_('Freios'),
  N_('Bateria'), N_('Filtros'), N_('Correia dentada'), N_('Ar-condicionado'), N_('Outro')];
// Intervalos de referência (km e meses). O manual do veículo sempre vale mais.
const INTERVALO: Record<string, [number, number]> = {
  'Troca de óleo': [10000, 12], 'Rodízio de pneus': [10000, 0], 'Revisão': [10000, 12], 'Alinhamento e balanceamento': [10000, 12],
  'Filtros': [10000, 12], 'Correia dentada': [50000, 48], 'Freios': [20000, 0], 'Ar-condicionado': [0, 12],
};
const ICONE: Record<string, string> = { 'Troca de óleo': '🛢️', 'Rodízio de pneus': '🔄', 'Pneus novos': '🛞', 'Revisão': '🔧', 'Freios': '🛑', 'Bateria': '🔋', 'Alinhamento e balanceamento': '⚙️' };
const VENC = [N_('IPVA'), N_('Licenciamento'), N_('Seguro'), N_('Financiamento'), N_('Multa'), N_('Inspeção'), N_('Outro')];

const kmAtual = (ctx: Ctx, r: Reg) => num(ctx.porId(r.pai)?.c.km);
const kmFmt = (n: number) => `${Math.round(n).toLocaleString()} km`;
/** Situação de uma manutenção pela quilometragem: "faltam 1.200 km" ou "passou 300 km". */
function faltaKm(ctx: Ctx, r: Reg) {
  if (!temNum(r.c.proxKm) || r.c.feita) return null;
  const k = kmAtual(ctx, r);
  if (!k) return null;
  const f = num(r.c.proxKm) - k;
  return { f, txt: f >= 0 ? t('faltam {km}', { km: kmFmt(f) }) : t('passou {km}', { km: kmFmt(-f) }) };
}

/** Atualizar a quilometragem do veículo e ver o que está perto de vencer. */
function PainelKm({ ctx, pai, lista }: PainelProps) {
  const [km, setKm] = useState('');
  if (!pai) return null;
  const proximas = lista.map(r => ({ r, s: faltaKm(ctx, r) })).filter(x => x.s && x.s.f <= 1000).sort((a, b) => a.s!.f - b.s!.f);
  const salvar = () => { const v = lerValor(km); if (isFinite(v) && v > 0) { mudarCampos(ctx.mod, pai.id, { km: Math.round(v), kmEm: today() }); setKm(''); } };
  return (
    <View style={{ backgroundColor: C.card, borderRadius: 14, padding: 12, gap: 8 }}>
      <Text style={{ color: C.text, fontWeight: '800' }}>🧭 {t('Quilometragem: {km}', { km: num(pai.c.km) ? kmFmt(num(pai.c.km)) : '—' })}{pai.c.kmEm ? <Text style={{ color: C.mute, fontWeight: '400', fontSize: 12 }}> · {dataBr(pai.c.kmEm)}</Text> : null}</Text>
      <View style={{ flexDirection: 'row', gap: 8 }}>
        <Field value={km} onChangeText={setKm} keyboardType="number-pad" placeholder={t('Km de hoje no painel')} style={{ flex: 1, minWidth: 0 }} onSubmitEditing={salvar} />
        <Pressable onPress={salvar} style={{ backgroundColor: ctx.cor, borderRadius: 12, paddingHorizontal: 16, justifyContent: 'center' }}><Text style={{ color: '#fff', fontWeight: '800' }}>{t('Atualizar')}</Text></Pressable>
      </View>
      {proximas.map(({ r, s }) => <Text key={r.id} style={{ color: s!.f < 0 ? C.danger : C.warn, fontSize: 13, fontWeight: '700' }}>{s!.f < 0 ? '⚠️' : '⏳'} {op(r.c.tipo)}: {s!.txt}</Text>)}
    </View>
  );
}

const veiculo: Colecao = {
  id: 'veiculo', nome: N_('Veículos'), um: N_('Veículo'), icone: r => (r.c.tipo === 'Moto' ? '🏍️' : r.c.tipo === 'Caminhão' ? '🚚' : '🚗'),
  campos: [
    { k: 'nome', r: N_('Nome ou modelo'), t: 'texto', ph: N_('Ex.: Onix 2022') },
    { k: 'tipo', r: N_('Tipo'), t: 'opcao', op: [N_('Carro'), N_('Moto'), N_('Caminhão'), N_('Outro')] },
    { k: 'placa', r: N_('Placa'), t: 'texto' },
    { k: 'ano', r: N_('Ano'), t: 'numero' },
    { k: 'km', r: N_('Quilometragem atual'), t: 'numero', un: 'km' },
    { k: 'combustivel', r: N_('Combustível'), t: 'opcao', op: [N_('Gasolina'), N_('Etanol'), N_('Flex'), N_('Diesel'), N_('Elétrico'), N_('Híbrido')] },
    { k: 'renavam', r: N_('Renavam ou chassi'), t: 'cofre' },
    { k: 'notas', r: N_('Observações'), t: 'longo' },
  ],
  titulo: r => r.c.nome || '', sub: r => junta(r.c.placa, num(r.c.km) ? kmFmt(num(r.c.km)) : ''), ordem: 'titulo', anexos: true,
  ajustar: (c, k) => (k === 'km' ? { ...c, kmEm: today() } : c),
  acoes: (r, ctx) => [acaoRelatorioPdf(() => relatorioVeiculo(ctx, r))],   // 0.65 (B15)
  vazio: N_('Cadastre cada veículo. Dentro dele ficam as manutenções (com aviso por km e por data) e os vencimentos de IPVA, licenciamento e seguro.'),
};

const manutencao: Colecao = {
  id: 'manutencao', pai: 'veiculo', nome: N_('Manutenções'), um: N_('Manutenção'), icone: r => ICONE[r.c.tipo] ?? '🔧',
  campos: [
    { k: 'tipo', r: N_('Serviço'), t: 'opcao', op: MANUT, ia: 'o serviço principal da nota ou da ordem de serviço' },
    { k: 'data', r: N_('Data em que foi feita'), t: 'data', ia: 'data de emissão da nota' },
    { k: 'km', r: N_('Km no dia'), t: 'numero', un: 'km', ia: 'quilometragem do veículo anotada na nota ou na ordem de serviço, se houver' },
    { k: 'valor', r: N_('Valor'), t: 'dinheiro', ia: 'valor total da nota (peças + mão de obra)' },
    { k: 'oficina', r: N_('Oficina'), t: 'texto', ia: 'nome da oficina, concessionária ou loja que emitiu a nota' },
    { k: 'endOficina', r: N_('Endereço da oficina'), t: 'endereco', ia: 'endereço do emitente da nota' },
    { k: 'proxKm', r: N_('Próxima com quantos km'), t: 'numero', un: 'km', ia: 'só se a nota ou etiqueta indicar a próxima troca/revisão por km' },
    { k: 'proxData', r: N_('Próxima até a data'), t: 'data', aviso: [15, 3], ia: 'só se a nota ou etiqueta indicar a data da próxima troca/revisão' },
    { k: 'notas', r: N_('Observações'), t: 'longo', ia: 'peças e serviços da nota, um por linha, com o valor de cada um' },
  ],
  titulo: r => op(r.c.tipo) || t('Manutenção'),
  sub: (r, ctx) => junta(dataBr(r.c.data), temNum(r.c.km) && kmFmt(num(r.c.km)), r.c.valor !== '' && r.c.valor != null && din(r.c.valor, ctx.moeda), faltaKm(ctx, r)?.txt, r.c.oficina),
  data: 'proxData', ordem: (a, b) => String(b.c.data || '').localeCompare(String(a.c.data || '')), anexos: true,
  novo: (ctx, pai) => ({ data: today(), km: pai?.c.km ?? '' }),
  // ao escolher o serviço, sugere a próxima pelo intervalo de referência
  ajustar: (c, k) => {
    const iv = INTERVALO[c.tipo];
    if (!iv || (k !== 'tipo' && k !== 'km' && k !== 'data')) return c;
    const o = { ...c };
    if (iv[0] && temNum(c.km)) o.proxKm = num(c.km) + iv[0];
    if (iv[1] && c.data) o.proxData = addDays(c.data, Math.round(iv[1] * 30.4));
    return o;
  },
  agenda: r => (r.c.proxData ? { start: r.c.proxData, title: t('{servico} do veículo', { servico: op(r.c.tipo) }) } : null),
  painel: PainelKm,
};

const vencimento: Colecao = {
  id: 'vencimento', check: 'pago', pagamento: { tipo: 'pagar', venc: 'data', data: 'pagoEm', valorPago: 'valorPago' }, pai: 'veiculo', nome: N_('Vencimentos'), um: N_('Vencimento'), icone: r => (r.c.pago ? '✅' : '📄'),
  campos: [
    { k: 'tipo', r: N_('Tipo'), t: 'opcao', op: VENC },
    { k: 'desc', r: N_('Descrição'), t: 'texto', ph: N_('Ex.: IPVA 2027, parcela 1'), ia: 'ex.: IPVA 2027, parcela 1; apólice do seguro' },
    { k: 'data', r: N_('Vencimento'), t: 'data', aviso: [15, 3], ia: 'data de vencimento da guia, boleto ou apólice' },
    { k: 'valor', r: N_('Valor'), t: 'dinheiro', ia: 'valor a pagar' },
    { k: 'pago', r: N_('Pago'), t: 'sim' },
    { k: 'pagoEm', r: N_('Data do pagamento'), t: 'data', mostrar: c => !!c.pago },
    { k: 'valorPago', r: N_('Valor pago'), t: 'dinheiro', mostrar: c => !!c.pago },
  ],
  titulo: r => junta(op(r.c.tipo), r.c.desc), sub: (r, ctx) => junta(r.c.valor !== '' && r.c.valor != null && dinDe(r, 'valor', ctx.moeda), r.c.pago && '✓ ' + t('pago')),
  data: 'data', feito: r => !!r.c.pago, anexos: true,
  agenda: (r, ctx) => ({ title: junta(op(r.c.tipo), ctx.porId(r.pai)?.c.nome) }),
  filtros: [[N_('Em aberto'), r => !r.c.pago], [N_('Pagos'), r => !!r.c.pago]],
};

const abastecimento: Colecao = {
  id: 'abastecimento', pai: 'veiculo', nome: N_('Abastecimentos'), um: N_('Abastecimento'), icone: '⛽',
  campos: [
    { k: 'data', r: N_('Data'), t: 'data', ia: 'data da nota ou do cupom do posto' },
    { k: 'km', r: N_('Km no painel'), t: 'numero', un: 'km', ia: 'quilometragem, só se estiver anotada no cupom' },
    { k: 'litros', r: N_('Litros'), t: 'numero', ia: 'quantidade de litros (ou m³ de GNV) abastecida' },
    { k: 'valor', r: N_('Valor'), t: 'dinheiro', ia: 'valor total pago' },
    { k: 'posto', r: N_('Posto'), t: 'texto', ia: 'nome do posto que emitiu a nota' },
  ],
  titulo: r => dataBr(r.c.data), sub: (r, ctx) => junta(temNum(r.c.litros) && `${num(r.c.litros)} L`, din(r.c.valor, ctx.moeda), temNum(r.c.km) && kmFmt(num(r.c.km)), r.c.posto),
  ordem: 'data-desc', novo: () => ({ data: today() }), anexos: true,
  resumo: l => {
    const c = l.filter(r => temNum(r.c.km) && temNum(r.c.litros)).sort((a, b) => num(a.c.km) - num(b.c.km));
    if (c.length < 2) return null;
    const km = num(c[c.length - 1].c.km) - num(c[0].c.km), litros = c.slice(1).reduce((s, r) => s + num(r.c.litros), 0);
    return litros > 0 && km > 0 ? t('Consumo médio: {v} km/L', { v: (km / litros).toFixed(1) }) : null;
  },
  aoSalvar: (r, ctx) => { const p = ctx.porId(r.pai); if (p && num(r.c.km) > num(p.c.km)) mudarCampos(ctx.mod, p.id, { km: num(r.c.km), kmEm: r.c.data || today() }); },
};

/** 0.65 (B15): relatório do veículo — custos, manutenções, abastecimentos e documentos (vencimentos). */
function relatorioVeiculo(ctx: Ctx, v: Reg) {
  const $ = (x: unknown) => din(x, ctx.moeda);
  const temV = (x: unknown) => x !== '' && x != null;
  const kmTxt = (x: unknown) => (temNum(x) ? kmFmt(num(x)) : '');
  const man = porDataPdf(ctx.filhos(v.id, 'manutencao'), 'data', true);
  const ab = porDataPdf(ctx.filhos(v.id, 'abastecimento'), 'data', true);
  const venc = porDataPdf(ctx.filhos(v.id, 'vencimento'), 'data', true);
  const tot = (l: Reg[]) => l.reduce((s, r) => s + num(r.c.valor), 0);
  const pagos = venc.filter(r => r.c.pago), abertos = venc.filter(r => !r.c.pago);
  // 0.69 (B1): cada vencimento pode ter a sua moeda; os totais ficam separados por moeda (não converte)
  const totV = (l: Reg[]) => somaPorMoeda(l, r => num(r.c.valor), ctx.moeda);
  const totais = totV(pagos); totais.set(ctx.moeda, (totais.get(ctx.moeda) ?? 0) + tot(man) + tot(ab));
  const custos = [[t('Manutenções'), $(tot(man))], [t('Abastecimentos'), $(tot(ab))], [junta(t('Vencimentos'), t('Pagos')), dinSomas(totV(pagos), ctx.moeda)]];
  custos.push([t('Total'), dinSomas(totais, ctx.moeda)]);
  if (abertos.length) custos.push([junta(t('Vencimentos'), t('Em aberto')), dinSomas(totV(abertos), ctx.moeda)]);
  const consumo = abastecimento.resumo?.(ab, ctx, v);
  const b: BlocoPdf[] = [
    { t: 'titulo', texto: junta(v.c.nome || t('Veículo'), v.c.placa) },
    ...dadosPdf([[t('Tipo'), op(v.c.tipo)], [t('Ano'), temNum(v.c.ano) ? String(num(v.c.ano)) : ''], [t('Combustível'), op(v.c.combustivel)],
      [t('Quilometragem atual'), junta(kmTxt(v.c.km), v.c.kmEm && dataBr(v.c.kmEm))]]),
    ...secaoPdf(t('Custos'), [t('Categoria'), t('Valor')], custos, [0.6, 0.4]),
    ...(consumo ? [{ t: 'par' as const, texto: consumo, tam: 10 }] : []),
    ...secaoPdf(t('Manutenções'), [t('Data'), t('Serviço'), t('Km no dia'), t('Oficina'), t('Valor'), t('Próxima')],
      man.map(r => [dataBr(r.c.data), op(r.c.tipo), kmTxt(r.c.km), r.c.oficina || '', temV(r.c.valor) ? $(r.c.valor) : '',
        r.c.feita ? '' : junta(kmTxt(r.c.proxKm), dataBr(r.c.proxData))]), [0.12, 0.22, 0.13, 0.19, 0.13, 0.21]),
    ...secaoPdf(t('Abastecimentos'), [t('Data'), t('Km no painel'), t('Litros'), t('Valor')],
      ab.map(r => [dataBr(r.c.data), kmTxt(r.c.km), temNum(r.c.litros) ? `${num(r.c.litros)} L` : '', $(r.c.valor)]), [0.25, 0.25, 0.25, 0.25]),
    ...secaoPdf(t('Vencimentos'), [t('Vencimento'), t('Tipo'), t('Descrição'), t('Valor'), t('Pago')],
      venc.map(r => [dataBr(r.c.data), op(r.c.tipo), r.c.desc || '', temV(r.c.valor) ? dinDe(r, 'valor', ctx.moeda) : '', r.c.pago ? t('Sim') : t('Não')]), [0.15, 0.18, 0.37, 0.15, 0.15]),
  ];
  return { nome: junta(t('Veículo'), v.c.nome, v.c.placa), blocos: b };
}

export const VEICULOS: ModDef = { id: 'veiculos', colecoes: [veiculo, manutencao, vencimento, abastecimento] };
